import { Typography, Box, Grid, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import CharacterCard from '../../../components/virtual/CharacterCard';
import { stakerIndexStyle } from '../index.styles';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getVirtuals } from '../../../api/virtual.api';
import { usePrivy } from '@privy-io/react-auth';
import { useInView } from 'react-intersection-observer';
import {
  Emission,
  PaginationResponse,
  Virtual,
  VirtualStatsDTO,
} from '../../../interfaces';
import WithdrawDrawer from './components/WithdrawDrawer';
import { getStakeVirtuals } from '../../../api/stake.api';
import LoginBanner from '../../login/LoginBanner';
import { getLatestEmission } from '../../../api/emission.api';
import { ethers } from 'ethers';
import useStakedList from '../../../hooks/useStakedList';
import { useVirtualBalanceContext } from '../../../context/VirtualBalanceContext';
import { calculateApr } from '../../../shared/utils/CalculateApr';

const WithdrawIndex = () => {
  const { ref, inView } = useInView();
  const { ready, authenticated, user, login } = usePrivy();

  const { xVirtualTotalSupply, isLoading: fetchingVirtualData } =
    useVirtualBalanceContext();
  const {
    totalSharedAmount,
    totalDeposit,
    isLoading: fetchingStakedList,
  } = useStakedList();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedVirtual, setSelectedVirtual] = useState<Virtual>(
    {} as Virtual
  );
  const [selectedVirtualId, setSelectedVirtualId] = useState<number>(0);
  const [totalDelegate, setTotalDelegate] = useState<BigInt>(BigInt(0));
  const [selectedVirtualUid, setSelectedVirtualUid] = useState<string>('');
  const [selectedVirtualStats, setSelectedVirtualStats] =
    useState<VirtualStatsDTO>();
  const [virtualFilter, setVirtualFilter] = useState<string>();
  const [hasStaked, setHasStaked] = useState<boolean>(false);
  const [virtualSort, setVirtualSort] = useState<string>('virtualLocked:desc');
  const [virtuals, setVirtuals] = useState<Virtual[]>();
  const [totalEmission, setTotalEmission] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: stakeVirtuals } = useQuery({
    queryKey: ['getStakeVirtuals', user?.wallet?.address],
    queryFn: () => getStakeVirtuals(user?.wallet?.address || ''),
  });

  const { data: emissions, isLoading: emissionIsLoading } = useInfiniteQuery({
    queryKey: ['dailyEmission'],
    queryFn: ({ pageParam }) => getLatestEmission(),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.meta.pagination.page + 1;
      return nextPage > lastPage.meta.pagination.pageCount
        ? undefined
        : nextPage;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      return firstPage.meta.pagination.page - 1;
    },
  });

  const { fetchNextPage, data } = useInfiniteQuery({
    enabled: stakeVirtuals?.data !== undefined,
    queryKey: ['virtualsWithBase', virtualFilter, virtualSort],
    queryFn: ({ pageParam }) =>
      getVirtuals({
        id: stakeVirtuals?.data?.map((d) => +d.virtualId),
        name: virtualFilter,
        sortBy: virtualSort,
        page: pageParam,
        status: 'AVAILABLE',
        displayBase: true,
        pageSize: 100,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.meta.pagination.page + 1;
      return nextPage > lastPage.meta.pagination.pageCount
        ? undefined
        : nextPage;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      return firstPage.meta.pagination.page - 1;
    },
  });

  const processOnChainDetails = async (
    virtualsData: PaginationResponse<Virtual[]>[]
  ) => {
    let tmpVirtuals: Virtual[] = [];
    setIsLoading(true);

    const totalSharedAmountDeposit = Number(totalSharedAmount);
    const totalxVirtualSupply = Number(ethers.formatEther(xVirtualTotalSupply));
    const totalDepositAmount = Number(totalDeposit);

    for (const page of virtualsData) {
      for (const o of page.data) {
        const revenue = calculateApr(
          totalEmission,
          totalSharedAmountDeposit,
          totalxVirtualSupply,
          totalDepositAmount
        );

        tmpVirtuals.push({
          ...o,
          revenue,
        });
      }
    }

    setVirtuals(tmpVirtuals);

    setIsLoading(false);

    return tmpVirtuals;
  };

  useEffect(() => {
    if (emissions && emissions?.pages?.length > 0) {
      let total = 0;
      emissions.pages.map((page: PaginationResponse<Emission[]>) => {
        page.data.map((o: Emission) => {
          return (total += o.amount);
        });
        // use reduce
      });

      const emission = total * 0.9 * 0.5 * 0.9;

      setTotalEmission(emission);
    }
  }, [emissions]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  useEffect(() => {
    if (!xVirtualTotalSupply) return;
    if (data && data?.pages?.length > 0) {
      processOnChainDetails(data.pages)
        .then((tmpVirtuals) => {})
        .catch((error) => {
          console.error('Error processing on-chain details:', error);
        });
    }
  }, [data, totalEmission, fetchingStakedList, xVirtualTotalSupply]);

  return (
    <Stack m={3}>
      {!user ? (
        <Stack>
          <LoginBanner title="Login to view this page." />
        </Stack>
      ) : (
        <>
          <Box sx={stakerIndexStyle.searchBarWrapperStyle}>
            <Typography sx={stakerIndexStyle.titleStyle}>
              Withdraw from a VIRTUAL
            </Typography>
          </Box>

          <Box mx={0}>
            <Grid container spacing={2}>
              {stakeVirtuals?.data &&
                stakeVirtuals?.data?.map((d, index) => {
                  var data = virtuals?.find((item: Virtual) => {
                    return item.id === Number(d.virtualId);
                  });

                  if (
                    data === undefined ||
                    data?.cores === undefined ||
                    d.totalStaked === '0'
                  )
                    return null;

                  if (hasStaked === false) {
                    setHasStaked(true);
                  }

                  return (
                    <Grid key={`${index}`} item xs={12} sm={6} lg={4} xl={3}>
                      <CharacterCard
                        {...data}
                        type="withdraw"
                        stake={d}
                        isAuth={authenticated}
                        onClick={() => {
                          if (ready && data) {
                            if (authenticated) {
                              setSelectedVirtual(data);
                              setSelectedVirtualId(data.id);
                              setSelectedVirtualUid(data.uid);
                              setSelectedVirtualStats(data.stats);
                              setTotalDelegate(BigInt(d.totalStaked));
                              setShowModal(true);
                            } else {
                              login();
                            }
                          }
                        }}
                      />
                    </Grid>
                  );
                })}
            </Grid>
            {!hasStaked && (
              <Stack display={'flex'} justifyContent={'center'} mt={2}>
                <Typography>
                  You have not staked to any virtuals yet.
                </Typography>
              </Stack>
            )}
          </Box>

          <WithdrawDrawer
            isOpen={showModal}
            virtual={selectedVirtual}
            totalDelegate={totalDelegate}
            onClose={() => setShowModal(false)}
          />
        </>
      )}
    </Stack>
  );
};

export default WithdrawIndex;
